// src/components/SubscriptionManagement.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { getApiBaseUrl } from '../utils/apiConfig';
import {
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
// Progressive migration: Start with optimized hook, fallback to basic if needed
import useOptimizedSubscription from '../hooks/useOptimizedSubscription';
import useSubscription from '../hooks/useSubscription';
import useTrialStatus from '../hooks/useTrialStatus';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import MemoizedPlanComparison from './MemoizedPlanComparison';
import MonitoringDashboard from './MonitoringDashboard';
import apiClient from '../api/client';

const SubscriptionManagement = ({ onNotification }) => {
  // Progressive enhancement: Use optimized hook with fallback to basic hook
  const [useOptimizedHook, setUseOptimizedHook] = useState(true);
  
  // Primary hook (optimized with performance monitoring)
  const optimizedHookData = useOptimizedSubscription();
  
  // Fallback hook (basic, reliable)
  const basicHookData = useSubscription();
  
  // Choose which hook data to use based on state and errors
  const hookData = useOptimizedHook ? optimizedHookData : basicHookData;
  
  const { 
    subscription, 
    loading, 
    error, 
    cancelSubscription, 
    reactivateSubscription
  } = hookData;
  
  // Fallback to original hook if optimized version fails
  // const fallbackHook = useSubscription();
  // const subscription_data = subscription || fallbackHook.subscription;
  // const subscription_loading = loading !== undefined ? loading : fallbackHook.loading;
  // const subscription_error = error || fallbackHook.error;
  
  const trialStatus = useTrialStatus();
  // const token = useAuthToken();
  
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  const [showMonitoringDashboard, setShowMonitoringDashboard] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  
  // Auto-fallback to basic hook if optimized hook has persistent errors
  useEffect(() => {
    if (useOptimizedHook && error && optimizedHookData.retryCount >= 2) {
      console.warn('🔄 Optimized hook failing, switching to basic hook for reliability');
      setUseOptimizedHook(false);
      onNotification({
        message: 'Switching to basic mode for better reliability',
        severity: 'info'
      });
    }
  }, [useOptimizedHook, error, optimizedHookData.retryCount, onNotification]);

  // Helper functions
  const isActive = subscription?.status === 'active' && !subscription?.cancel_at_period_end;
  const isCanceled = subscription?.status === 'canceled' || subscription?.cancel_at_period_end;
  const isTrialing = subscription?.status === 'trialing';
  const canCancel = ['active', 'trialing'].includes(subscription?.status) && !subscription?.cancel_at_period_end;
  const canReactivate = (subscription?.status === 'canceled' && subscription?.can_reactivate) || subscription?.cancel_at_period_end;

  const handleCancelSubscription = async (reason, feedback, cancelImmediately) => {
    try {
      const result = await cancelSubscription(reason, feedback, cancelImmediately);
      
      onNotification({
        message: result.message || 'Subscription canceled successfully',
        severity: 'success'
      });
      
      setShowCancelModal(false);
    } catch (error) {
      onNotification({
        message: error.message || 'Failed to cancel subscription',
        severity: 'error'
      });
    }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading(true);
    setLastError(null);
    
    // Check network connectivity
    if (!navigator.onLine) {
      onNotification({
        message: 'No internet connection. Please check your connection and try again.',
        severity: 'error'
      });
      setActionLoading(false);
      return;
    }
    
    try {
      const result = await reactivateSubscription();
      
      onNotification({
        message: result.message || 'Subscription reactivated successfully',
        severity: 'success'
      });
    } catch (error) {
      setLastError(error);
      
      // Enhanced error handling with specific error messages
      let errorMessage = 'Failed to reactivate subscription';
      let canRetry = false;
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            if (data?.detail?.includes('payment method')) {
              errorMessage = 'Please add a valid payment method before reactivating. Click "Manage Billing" to add payment details.';
            } else if (data?.detail?.includes('cannot be reactivated')) {
              errorMessage = 'This subscription cannot be reactivated. Please contact support for assistance.';
            } else {
              errorMessage = data?.detail || 'Cannot reactivate subscription';
            }
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and log in again.';
            break;
          case 403:
            errorMessage = 'Your account doesn\'t have permission to reactivate. Please contact support.';
            break;
          case 404:
            errorMessage = 'Subscription not found. Please refresh the page and try again.';
            break;
          case 409:
            errorMessage = 'Your subscription is already active or a billing change is in progress.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a few minutes and try again.';
            canRetry = true;
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = 'Service temporarily unavailable. Please try again in a few minutes.';
            canRetry = true;
            break;
          default:
            errorMessage = data?.detail || error.message || 'An unexpected error occurred';
            canRetry = status >= 500;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
        canRetry = true;
      } else {
        // Other error
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      onNotification({
        message: errorMessage,
        severity: 'error',
        action: canRetry && retryCount < 2 ? {
          label: 'Retry',
          onClick: () => {
            setRetryCount(prev => prev + 1);
            setTimeout(() => handleReactivateSubscription(), 1000);
          }
        } : undefined
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = async (plan, proration) => {
    setActionLoading(true);
    setLastError(null);
    
    // Validate plan data before proceeding
    if (!plan || !plan.name || !plan.priceId) {
      onNotification({
        message: 'Invalid plan configuration. Please refresh the page and try again.',
        severity: 'error'
      });
      setActionLoading(false);
      return;
    }
    
    // Check network connectivity
    if (!navigator.onLine) {
      onNotification({
        message: 'No internet connection. Please check your connection and try again.',
        severity: 'error'
      });
      setActionLoading(false);
      return;
    }
    
    try {
      console.log('🔍 Current subscription data:', subscription);
      console.log('🔍 Upgrade plan data:', {
        name: plan.name,
        displayName: plan.display_name,
        billingPeriod: plan.billingPeriod,
        priceId: plan.priceId,
        fullPlan: plan
      });
      
      await apiClient.post('/billing/upgrade-subscription', {
        target_plan: plan.name,
        billing_period: plan.billingPeriod || 'monthly',
        price_id: plan.priceId
      });
      
      // Show success message
      onNotification({
        message: `Successfully upgraded to ${plan.display_name}! Updating your account...`,
        severity: 'success'
      });
      
      // Wait for webhook to process and handle JWT invalidation gracefully
      setTimeout(async () => {
        try {
          let retryCount = 0;
          const maxRetries = 5;
          const retryDelay = 1000; // Start with 1 second delay
          
          const attemptTokenRefresh = async () => {
            const currentToken = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
            
            if (!currentToken) {
              console.log('No token found, user already logged out');
              window.location.reload();
              return;
            }
            
            try {
              const refreshResponse = await fetch(`${getApiBaseUrl()}/auth/refresh-token`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${currentToken}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json();
                localStorage.setItem('authToken', refreshData.access_token);
                localStorage.removeItem('auth_token');
                
                onNotification({
                  message: `Successfully upgraded to ${plan.display_name}! Your new features are now active.`,
                  severity: 'success'
                });
                
                window.location.reload();
                return true;
              } else if (refreshResponse.status === 401) {
                // Token was invalidated by webhook - this is expected
                console.log(`Token refresh attempt ${retryCount + 1}: Token invalidated, retrying...`);
                throw new Error('Token invalidated');
              } else {
                console.error('Token refresh failed with status:', refreshResponse.status);
                throw new Error('Token refresh failed');
              }
            } catch (error) {
              if (retryCount < maxRetries - 1) {
                retryCount++;
                console.log(`Token refresh attempt ${retryCount} failed, retrying in ${retryDelay * retryCount}ms...`);
                await new Promise(resolve => setTimeout(resolve, retryDelay * retryCount));
                return attemptTokenRefresh();
              } else {
                // Final fallback after all retries
                console.log('All token refresh attempts failed, performing graceful reload');
                onNotification({
                  message: `Successfully upgraded to ${plan.display_name}! Please refresh if you don't see updates.`,
                  severity: 'success'
                });
                window.location.reload();
                return false;
              }
            }
          };
          
          await attemptTokenRefresh();
          
        } catch (error) {
          console.error('Error in token refresh process:', error);
          window.location.reload();
        }
      }, 3000); // Increased delay to 3 seconds
      
    } catch (error) {
      setLastError(error);
      
      // Enhanced error handling with specific error messages
      let errorMessage = 'Failed to upgrade subscription';
      let canRetry = false;
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            if (data?.detail?.includes('payment method')) {
              errorMessage = 'Please add a valid payment method before upgrading. Click "Manage Billing" to add payment details.';
            } else if (data?.detail?.includes('not found')) {
              errorMessage = 'The selected plan is no longer available. Please refresh the page and try again.';
            } else {
              errorMessage = data?.detail || 'Invalid upgrade request';
            }
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and log in again.';
            break;
          case 402:
            errorMessage = 'Payment is required. Please check your payment method and try again.';
            break;
          case 403:
            errorMessage = 'Your account doesn\'t have permission to upgrade. Please contact support.';
            break;
          case 404:
            errorMessage = 'Subscription not found. Please refresh the page and try again.';
            break;
          case 409:
            errorMessage = 'A billing change is already in progress. Please wait a moment and try again.';
            canRetry = true;
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a few minutes and try again.';
            canRetry = true;
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = 'Service temporarily unavailable. Please try again in a few minutes.';
            canRetry = true;
            break;
          default:
            errorMessage = data?.detail || error.message || 'An unexpected error occurred';
            canRetry = status >= 500;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
        canRetry = true;
      } else {
        // Other error
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      onNotification({
        message: errorMessage,
        severity: 'error',
        action: canRetry && retryCount < 2 ? {
          label: 'Retry',
          onClick: () => {
            setRetryCount(prev => prev + 1);
            setTimeout(() => handleUpgrade(plan, proration), 1000);
          }
        } : undefined
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Removed pollSubscriptionStatus function - was causing timeout issues

  const handleDowngrade = async (plan, proration) => {
    setActionLoading(true);
    setLastError(null);
    
    // Validate plan data before proceeding
    if (!plan || !plan.name || !plan.priceId) {
      onNotification({
        message: 'Invalid plan configuration. Please refresh the page and try again.',
        severity: 'error'
      });
      setActionLoading(false);
      return;
    }
    
    // Check network connectivity
    if (!navigator.onLine) {
      onNotification({
        message: 'No internet connection. Please check your connection and try again.',
        severity: 'error'
      });
      setActionLoading(false);
      return;
    }
    
    try {
      const result = await apiClient.post('/billing/downgrade-subscription', {
        target_plan: plan.name,
        reason: 'user_requested',
        price_id: plan.priceId
      });
      
      if (!result.success && result.validation_warnings) {
        // Show validation warnings
        onNotification({
          message: `Downgrade requires acknowledgment: ${result.validation_warnings.join(', ')}`,
          severity: 'warning'
        });
        return;
      }
      
      // Show success message
      onNotification({
        message: `Successfully downgraded to ${plan.display_name}. Updating your account...`,
        severity: 'success'
      });
      
      // Wait for webhook to process and handle JWT invalidation gracefully
      setTimeout(async () => {
        try {
          let retryCount = 0;
          const maxRetries = 5;
          const retryDelay = 1000; // Start with 1 second delay
          
          const attemptTokenRefresh = async () => {
            const currentToken = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
            
            if (!currentToken) {
              console.log('No token found, user already logged out');
              window.location.reload();
              return;
            }
            
            try {
              const refreshResponse = await fetch(`${getApiBaseUrl()}/auth/refresh-token`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${currentToken}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json();
                localStorage.setItem('authToken', refreshData.access_token);
                localStorage.removeItem('auth_token');
                
                onNotification({
                  message: `Successfully downgraded to ${plan.display_name}! Changes are now active.`,
                  severity: 'success'
                });
                
                window.location.reload();
                return true;
              } else if (refreshResponse.status === 401) {
                // Token was invalidated by webhook - this is expected
                console.log(`Token refresh attempt ${retryCount + 1}: Token invalidated, retrying...`);
                throw new Error('Token invalidated');
              } else {
                console.error('Token refresh failed with status:', refreshResponse.status);
                throw new Error('Token refresh failed');
              }
            } catch (error) {
              if (retryCount < maxRetries - 1) {
                retryCount++;
                console.log(`Token refresh attempt ${retryCount} failed, retrying in ${retryDelay * retryCount}ms...`);
                await new Promise(resolve => setTimeout(resolve, retryDelay * retryCount));
                return attemptTokenRefresh();
              } else {
                // Final fallback after all retries
                console.log('All token refresh attempts failed, performing graceful reload');
                onNotification({
                  message: `Successfully downgraded to ${plan.display_name}! Please refresh if you don't see updates.`,
                  severity: 'success'
                });
                window.location.reload();
                return false;
              }
            }
          };
          
          await attemptTokenRefresh();
          
        } catch (error) {
          console.error('Error in token refresh process:', error);
          window.location.reload();
        }
      }, 3000); // Increased delay to 3 seconds
      
    } catch (error) {
      setLastError(error);
      
      // Enhanced error handling with specific error messages
      let errorMessage = 'Failed to downgrade subscription';
      let canRetry = false;
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            if (data?.detail?.includes('already at')) {
              errorMessage = 'You are already on the lowest available plan.';
            } else {
              errorMessage = data?.detail || 'Invalid downgrade request';
            }
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and log in again.';
            break;
          case 403:
            errorMessage = 'Your account doesn\'t have permission to downgrade. Please contact support.';
            break;
          case 404:
            errorMessage = 'Subscription not found. Please refresh the page and try again.';
            break;
          case 409:
            errorMessage = 'A billing change is already in progress. Please wait a moment and try again.';
            canRetry = true;
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a few minutes and try again.';
            canRetry = true;
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = 'Service temporarily unavailable. Please try again in a few minutes.';
            canRetry = true;
            break;
          default:
            errorMessage = data?.detail || error.message || 'An unexpected error occurred';
            canRetry = status >= 500;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
        canRetry = true;
      } else {
        // Other error
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      onNotification({
        message: errorMessage,
        severity: 'error',
        action: canRetry && retryCount < 2 ? {
          label: 'Retry',
          onClick: () => {
            setRetryCount(prev => prev + 1);
            setTimeout(() => handleDowngrade(plan, proration), 1000);
          }
        } : undefined
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (isTrialing) {
      return {
        status: 'Trial Active',
        color: 'info',
        icon: <ScheduleIcon />,
        description: `${trialStatus.daysRemaining || 'N/A'} days remaining in trial`
      };
    }
    
    if (isActive) {
      return {
        status: 'Active',
        color: 'success',
        icon: <CheckCircleIcon />,
        description: 'Your subscription is active and current'
      };
    }
    
    if (isCanceled) {
      if (subscription?.cancel_at_period_end) {
        const endDate = new Date(subscription.current_period_end);
        const now = new Date();
        const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        const maxDays = Math.max(0, daysRemaining);
        
        return {
          status: 'Ending Soon',
          color: 'warning',
          icon: <ScheduleIcon />,
          description: maxDays === 0 
            ? `Access expires today (${endDate.toLocaleDateString()})`
            : maxDays === 1
            ? `Access expires tomorrow (${endDate.toLocaleDateString()})`
            : `${maxDays} days remaining until ${endDate.toLocaleDateString()}`
        };
      } else {
        return {
          status: 'Canceled',
          color: 'error',
          icon: <CancelIcon />,
          description: 'Subscription has been canceled'
        };
      }
    }
    
    return {
      status: 'Unknown',
      color: 'default',
      icon: <WarningIcon />,
      description: 'Unable to determine subscription status'
    };
  };

  const statusInfo = getStatusInfo();

  // Enhanced loading state with timeout handling
  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading subscription information...</Typography>
        </Box>
        {/* Add retry option if loading takes too long */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Taking longer than expected?
          </Typography>
          <Button 
            size="small" 
            sx={{ ml: 1 }}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </Box>
      </Paper>
    );
  }

  // Enhanced error state with retry options
  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert 
          severity="error"
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                size="small" 
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
              {lastError?.response?.status >= 500 && (
                <Button 
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setRetryCount(0);
                    window.location.reload();
                  }}
                >
                  Retry
                </Button>
              )}
            </Box>
          }
        >
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            Unable to load subscription information
          </Typography>
          <Typography variant="body2">
            {error.includes('network') || error.includes('connection') 
              ? 'Please check your internet connection and try again.'
              : error.includes('session') || error.includes('expired')
              ? 'Your session has expired. Please refresh the page.'
              : error.includes('server') || error.includes('500')
              ? 'Our servers are temporarily unavailable. Please try again in a few minutes.'
              : `Error: ${error}`
            }
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCardIcon />
          Subscription & Billing
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Subscription Status Card */}
          <Grid size={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {statusInfo.icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Subscription Status
                  </Typography>
                </Box>
                
                <Chip 
                  label={statusInfo.status}
                  color={statusInfo.color}
                  variant="filled"
                  sx={{ mb: 2 }}
                />
                
                <Typography variant="body2" color="text.secondary">
                  {statusInfo.description}
                </Typography>

                {subscription && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Plan:</strong> {subscription.plan_name || 'Professional'}
                    </Typography>
                    {subscription.current_period_end && (
                      <Typography variant="body2">
                        <strong>Next billing:</strong> {new Date(subscription.current_period_end).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actions Card */}
          <Grid size={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Manage Subscription
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {canReactivate && (
                    <>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        You can reactivate your subscription to restore full access.
                      </Alert>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<RefreshIcon />}
                        onClick={handleReactivateSubscription}
                        disabled={actionLoading}
                        fullWidth
                      >
                        {actionLoading ? 'Reactivating...' : 'Reactivate Subscription'}
                      </Button>
                    </>
                  )}

                  {/* Change Plan button - now first */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setShowPlanComparison(!showPlanComparison);
                      // Scroll to plan comparison when showing
                      if (!showPlanComparison) {
                        setTimeout(() => {
                          const element = document.getElementById('plan-comparison-section');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          }
                        }, 100);
                      }
                    }}
                    fullWidth
                  >
                    {showPlanComparison ? 'Hide Protection Options' : 'Increase Protection Level'}
                  </Button>

                  {/* Manage Billing button - now second */}
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CreditCardIcon />}
                    onClick={async () => {
                      try {
                        setActionLoading(true);
                        
                        const data = await apiClient.post('/billing/billing-portal', {
                          return_url: window.location.href
                        });
                        
                        if (data.success && data.url) {
                          // Redirect to Stripe billing portal
                          window.location.href = data.url;
                        } else {
                          throw new Error('Invalid response from billing portal');
                        }
                      } catch (error) {
                        // Enhanced error handling for billing portal
                        let errorMessage = 'Failed to open billing portal';
                        
                        if (error.response) {
                          const status = error.response.status;
                          const data = error.response.data;
                          
                          switch (status) {
                            case 400:
                              errorMessage = 'Invalid billing portal request. Please try again.';
                              break;
                            case 401:
                              errorMessage = 'Your session has expired. Please refresh the page and log in again.';
                              break;
                            case 403:
                              errorMessage = 'Access denied. Please contact support for billing assistance.';
                              break;
                            case 404:
                              errorMessage = 'Billing portal not available. Please contact support.';
                              break;
                            case 429:
                              errorMessage = 'Too many requests. Please wait a moment and try again.';
                              break;
                            case 500:
                            case 502:
                            case 503:
                            case 504:
                              errorMessage = 'Billing system temporarily unavailable. Please try again in a few minutes.';
                              break;
                            default:
                              errorMessage = data?.detail || error.message || 'Unable to access billing portal';
                          }
                        } else if (error.request) {
                          errorMessage = 'Unable to connect to billing system. Please check your internet connection.';
                        } else {
                          errorMessage = error.message || 'An unexpected error occurred';
                        }
                        
                        onNotification({
                          message: errorMessage,
                          severity: 'error'
                        });
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    fullWidth
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Loading...' : 'Manage Billing'}
                  </Button>

                  {/* Development Monitoring Dashboard button */}
                  {process.env.NODE_ENV === 'development' && (
                    <Button
                      variant="outlined"
                      color="info"
                      startIcon={<RefreshIcon />}
                      onClick={() => setShowMonitoringDashboard(true)}
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Production Monitoring
                    </Button>
                  )}

                  {/* Cancel Subscription button - now last */}
                  {canCancel && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<CancelIcon />}
                      onClick={() => setShowCancelModal(true)}
                      fullWidth
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Enhanced Cancellation Information */}
        {isCanceled && subscription?.cancel_at_period_end && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <ScheduleIcon sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Your subscription is scheduled for cancellation
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Access expires:</strong> {new Date(subscription.current_period_end).toLocaleDateString()}
                  {(() => {
                    const endDate = new Date(subscription.current_period_end);
                    const now = new Date();
                    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                    const maxDays = Math.max(0, daysRemaining);
                    
                    if (maxDays === 0) {
                      return <span style={{ color: '#d32f2f', fontWeight: 600 }}> (expires today)</span>;
                    } else if (maxDays === 1) {
                      return <span style={{ color: '#ed6c02', fontWeight: 600 }}> (1 day remaining)</span>;
                    } else if (maxDays <= 7) {
                      return <span style={{ color: '#ed6c02', fontWeight: 600 }}> ({maxDays} days remaining)</span>;
                    } else {
                      return <span style={{ color: '#1976d2', fontWeight: 600 }}> ({maxDays} days remaining)</span>;
                    }
                  })()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  After expiration, your account will be in read-only mode for 30 days before data deletion.
                  You can reactivate anytime to restore full access.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}

        {isTrialing && trialStatus.daysRemaining <= 3 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Your trial is ending soon!</strong> 
              {' '}Add a payment method to avoid losing access to your data and features.
            </Typography>
          </Alert>
        )}

        {/* Plan Comparison Section - now inside the Paper */}
        {showPlanComparison && (
          <Box id="plan-comparison-section" sx={{ mt: 3 }}>
            <Divider sx={{ mb: 3 }} />
            <MemoizedPlanComparison
              onUpgrade={handleUpgrade}
              onDowngrade={handleDowngrade}
              onNotification={onNotification}
            />
          </Box>
        )}
      </Paper>

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        subscription={subscription}
        onCancel={handleCancelSubscription}
      />

      {/* Production Monitoring Dashboard */}
      <MonitoringDashboard
        open={showMonitoringDashboard}
        onClose={() => setShowMonitoringDashboard(false)}
      />
    </>
  );
};

export default SubscriptionManagement;